import { useCallback, useState } from "react";

const OUTCOME_ONLY_GROUP_LABEL = "All respondents";

const normalizeValue = (value) => String(value ?? "").trim();

const isMissingCategoryValue = (value) => {
  const normalized = normalizeValue(value).toLowerCase();
  return normalized === "" || normalized === "null" || normalized === "undefined";
};

const lookupMappingEntry = (mapping, rawKey) => {
  if (!mapping || !rawKey) return null;
  if (Object.prototype.hasOwnProperty.call(mapping, rawKey)) return mapping[rawKey];

  const keyByTrimMatch = Object.entries(mapping).find(
    ([mappingKey]) => normalizeValue(mappingKey) === rawKey
  );
  if (keyByTrimMatch) return keyByTrimMatch[1];

  const numericKey = Number(rawKey);
  if (Number.isFinite(numericKey)) {
    const fallbackKey = String(numericKey);
    if (Object.prototype.hasOwnProperty.call(mapping, fallbackKey)) return mapping[fallbackKey];

    const keyByNumericMatch = Object.entries(mapping).find(([mappingKey]) => {
      const normalizedKey = normalizeValue(mappingKey);
      if (!normalizedKey) return false;
      const normalizedNumericKey = Number(normalizedKey);
      return Number.isFinite(normalizedNumericKey) && normalizedNumericKey === numericKey;
    });
    if (keyByNumericMatch) return keyByNumericMatch[1];
  }

  return null;
};

const hasMappingEntries = (mapping) =>
  Boolean(mapping && typeof mapping === "object" && Object.keys(mapping).length > 0);

const resolveLabelFromMapping = (mapping, rawValue) => {
  const normalizedRaw = normalizeValue(rawValue);
  if (!normalizedRaw) return { label: "", matched: false };

  const entry = lookupMappingEntry(mapping, normalizedRaw);
  const mappedDisplay = normalizeValue(entry?.Display ?? entry?.display ?? "");
  return {
    label: mappedDisplay || normalizedRaw,
    matched: Boolean(entry),
  };
};

const buildGroupedDisplayLookup = (indepGroupMembers, indepGroupDisplay) => {
  const lookup = new Map();
  Object.entries(indepGroupMembers || {}).forEach(([groupId, members]) => {
    const display = normalizeValue(indepGroupDisplay?.[groupId] ?? groupId);
    (members || []).forEach((member) => {
      const normalizedMember = normalizeValue(member);
      if (normalizedMember) {
        lookup.set(normalizedMember, display || normalizedMember);
      }
    });
  });
  return lookup;
};

export const buildChartGraphData = ({
  sqlData,
  selectedIndependent,
  selectedDependent,
  selectedIndepMeta,
  selectedDepMeta,
  questionsIndex,
  dependentAnswerMapping,
  applyActiveFilters,
  indepIsGrouped,
  indepGroupMembers,
  indepGroupDisplay,
  getRowWeight,
}) => {
  if (!selectedDependent) {
    return { graphData: [], graphError: "Select an outcome." };
  }

  if (!Array.isArray(sqlData) || sqlData.length === 0) {
    return { graphData: [], graphError: "No data available for the selected study." };
  }

  const filteredData = applyActiveFilters(sqlData);
  if (!Array.isArray(filteredData)) {
    return { graphData: [], graphError: "Filtered data is not in a valid format." };
  }

  const hasComparison = Boolean(selectedIndependent);
  const independentType = hasComparison
    ? String(selectedIndepMeta?.type || "").toLowerCase()
    : "cat";
  const dependentType = String(selectedDepMeta?.type || "").toLowerCase();

  const independentMapping = hasComparison
    ? questionsIndex[selectedIndependent]?.answersMapping || {}
    : {};
  const dependentMapping = dependentAnswerMapping || {};
  const enforceIndependentMapping = hasComparison && hasMappingEntries(independentMapping);
  const enforceDependentMapping = hasMappingEntries(dependentMapping);
  const groupedDisplayLookup =
    hasComparison && independentType === "cat" && indepIsGrouped
      ? buildGroupedDisplayLookup(indepGroupMembers, indepGroupDisplay)
      : null;

  const tidyRows = [];
  for (const row of filteredData) {
    const independentRaw = hasComparison ? row[selectedIndependent] : OUTCOME_ONLY_GROUP_LABEL;
    const dependentRaw = row[selectedDependent];
    const normalizedGroupRaw = normalizeValue(independentRaw);
    const normalizedDepRaw = normalizeValue(dependentRaw);
    const rowWeight = getRowWeight(row);

    if (independentType === "cat" && hasComparison && isMissingCategoryValue(independentRaw)) {
      continue;
    }
    if (dependentType === "cat" && isMissingCategoryValue(dependentRaw)) {
      continue;
    }

    const groupResolution =
      hasComparison && enforceIndependentMapping
        ? resolveLabelFromMapping(independentMapping, normalizedGroupRaw)
        : { label: normalizedGroupRaw, matched: true };
    const depResolution = enforceDependentMapping
      ? resolveLabelFromMapping(dependentMapping, normalizedDepRaw)
      : dependentType === "cat"
        ? { label: normalizedDepRaw, matched: true }
        : { label: null, matched: true };

    if (hasComparison && enforceIndependentMapping && !groupResolution.matched) {
      continue;
    }
    if (enforceDependentMapping && !depResolution.matched) {
      continue;
    }

    const mappedGroupLabel =
      independentType === "cat" && hasComparison
        ? groupedDisplayLookup?.get(normalizedGroupRaw) || groupResolution.label
        : OUTCOME_ONLY_GROUP_LABEL;
    const mappedDepLabel = depResolution.label;

    const normalizedRow = {
      groupRaw: normalizedGroupRaw || null,
      group:
        independentType === "cat"
          ? hasComparison
            ? mappedGroupLabel || groupResolution.label || null
            : OUTCOME_ONLY_GROUP_LABEL
          : null,
      depRaw: normalizedDepRaw || null,
      dep: dependentType === "cat" ? mappedDepLabel || depResolution.label || null : null,
      x: independentType === "num" ? Number(independentRaw) : null,
      y: dependentType === "num" ? Number(dependentRaw) : null,
      weight: rowWeight,
    };

    if (independentType === "cat" && hasComparison && !normalizeValue(normalizedRow.group)) {
      continue;
    }
    if (dependentType === "cat" && !normalizeValue(normalizedRow.dep)) {
      continue;
    }
    if (independentType === "num" && !Number.isFinite(normalizedRow.x)) continue;
    if (dependentType === "num" && !Number.isFinite(normalizedRow.y)) continue;

    tidyRows.push(normalizedRow);
  }

  return {
    graphData: tidyRows,
    graphError: tidyRows.length ? null : "No records match the current configuration and filters.",
  };
};

export const useChartGraphData = ({
  sqlData,
  selectedIndependent,
  selectedDependent,
  useWeightedData,
  weightColumn,
  selectedIndepMeta,
  selectedDepMeta,
  questionsIndex,
  dependentAnswerMapping,
  applyActiveFilters,
  indepIsGrouped,
  indepGroupMembers,
  indepGroupDisplay,
}) => {
  const [graphData, setGraphData] = useState([]);
  const [graphError, setGraphError] = useState(null);

  const resetGraph = useCallback(() => {
    setGraphData([]);
    setGraphError(null);
  }, []);

  const getRowWeight = useCallback(
    (row) => {
      if (!useWeightedData || !weightColumn) return 1;
      const parsed = Number(row?.[weightColumn]);
      if (!Number.isFinite(parsed)) return 1;
      return Math.max(0, parsed);
    },
    [useWeightedData, weightColumn]
  );

  const generateGraph = useCallback(() => {
    try {
      setGraphError(null);
      const { graphData: nextGraphData, graphError: nextGraphError } = buildChartGraphData({
        sqlData,
        selectedIndependent,
        selectedDependent,
        selectedIndepMeta,
        selectedDepMeta,
        questionsIndex,
        dependentAnswerMapping,
        applyActiveFilters,
        indepIsGrouped,
        indepGroupMembers,
        indepGroupDisplay,
        getRowWeight,
      });
      setGraphData(nextGraphData);
      if (nextGraphError) setGraphError(nextGraphError);
    } catch (error) {
      setGraphData([]);
      setGraphError("Could not generate chart data. Please try again.");
    }
  }, [
    sqlData,
    applyActiveFilters,
    selectedIndepMeta,
    selectedDepMeta,
    questionsIndex,
    selectedIndependent,
    dependentAnswerMapping,
    selectedDependent,
    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
    getRowWeight,
  ]);

  return {
    graphData,
    graphError,
    generateGraph,
    resetGraph,
  };
};
